import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { API_HOST } from 'environments/environment';
import { take } from 'rxjs';
import { SnackbarService } from '../../services/snackbar.service';
import { AuthService } from '../../services/auth.service';
import { APIResponse } from 'fakturnia-shared';
import { Router } from '@angular/router';

declare global {
  interface Window {
    AppleID: any;
  }
}

@Component({
  selector: 'log-in-with-apple',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './log-in-with-apple.component.html',
  styleUrl: './log-in-with-apple.component.scss'
})
export class LogInWithAppleComponent implements OnInit {

  public isLoading = false;
  private _sdkLoaded = false;

  constructor(
    private _httpClient: HttpClient,
    private _snackbarService: SnackbarService,
    private _authService: AuthService,
    private _router: Router
  ) { }

  ngOnInit(): void {
    this._loadAppleSDK();
  }

  private _loadAppleSDK(): void {
    if (document.getElementById('apple-signin-sdk')) {
      this._sdkLoaded = true;
      return;
    }

    const script = document.createElement('script');
    script.id = 'apple-signin-sdk';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.onload = () => {
      this._sdkLoaded = true;
    };
    script.onerror = () => {
      console.error('Failed to load Apple Sign-In SDK');
    };
    document.head.appendChild(script);
  }

  public async loginWithApple(): Promise<void> {
    if (this.isLoading) return;

    if (!this._sdkLoaded || !window.AppleID) {
      this._snackbarService.showSnackBar(
        'Logowanie przez Apple jest chwilowo niedostępne. Odśwież stronę.',
        'error',
        4000
      );
      return;
    }

    this.isLoading = true;

    try {
      window.AppleID.auth.init({
        clientId: 'pl.fakturnia.auth', // Apple Services ID
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true
      });

      const response = await window.AppleID.auth.signIn();
      const idToken = response.authorization?.id_token;

      if (!idToken) {
        this._snackbarService.showSnackBar(
          'Nie otrzymano danych autoryzacji z Apple.',
          'error',
          4000
        );
        this.isLoading = false;
        return;
      }

      // Apple przesyła dane użytkownika (imię/nazwisko) tylko przy pierwszej autoryzacji
      const firstName = response.user?.name?.firstName || '';
      const lastName = response.user?.name?.lastName || '';

      // Wyślij ID token do zunifikowanego endpointu
      this._httpClient.post<APIResponse>(API_HOST + 'auth/social', {
        provider: 'apple',
        idToken,
        platform: 'web',
        firstName,
        lastName
      })
        .pipe(take(1))
        .subscribe({
          next: (apiResponse: APIResponse) => {
            if (!apiResponse.success) {
              this._snackbarService.showSnackBar(
                apiResponse.message || 'Nie udało się zalogować przez Apple.',
                'error',
                4000
              );
              this.isLoading = false;
              return;
            }

            // Zaloguj użytkownika
            (this._authService as any)._completeLogin(apiResponse.data, true);
          },
          error: () => {
            this._snackbarService.showSnackBar(
              'Wystąpił błąd podczas logowania przez Apple.',
              'error',
              4000
            );
            this.isLoading = false;
          }
        });
    } catch (err: any) {
      // Użytkownik anulował logowanie
      if (err?.error === 'popup_closed_by_user') {
        this.isLoading = false;
        return;
      }

      console.error('Apple sign-in error:', err);
      this._snackbarService.showSnackBar(
        'Wystąpił błąd podczas logowania przez Apple.',
        'error',
        4000
      );
      this.isLoading = false;
    }
  }
}
